/**
 * Entrada View Dialog - Visualização somente leitura de uma entrada
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { EntradaCompleta } from "@/types/entrada";
import { format } from "date-fns";
import { PermissionGate } from "@/components/PermissionGate";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface EntradaViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entrada: EntradaCompleta | null;
  onEdit: () => void;
}

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const getTipoMovimentoLabel = (tipo: string) => {
  const labels: Record<string, string> = {
    compra: "Compra",
    bonificacao: "Bonificação",
    devolucao_cliente: "Devolução Cliente",
    ajuste_positivo: "Ajuste Positivo",
  };
  return labels[tipo] || tipo;
};

export function EntradaViewDialog({
  open,
  onOpenChange,
  entrada,
  onEdit,
}: EntradaViewDialogProps) {
  if (!entrada) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0 pb-4">
          <DialogTitle>Visualizar Entrada</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 -mx-6 px-6">
        <div className="px-6 space-y-6">
          {/* Cabeçalho */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Dados do Documento</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Tipo de Movimento</span>
                <div className="mt-1">
                  <Badge>{getTipoMovimentoLabel(entrada.cabecalho.tipo_movimento)}</Badge>
                </div>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Data da Entrada</span>
                <p className="font-medium mt-1">
                  {format(new Date(entrada.cabecalho.data_entrada), 'dd/MM/yyyy')}
                </p>
              </div>
              {entrada.cabecalho.fornecedor_nome && (
                <div>
                  <span className="text-sm text-muted-foreground">Fornecedor</span>
                  <p className="font-medium mt-1">{entrada.cabecalho.fornecedor_nome}</p>
                </div>
              )}
              {entrada.cabecalho.documento_tipo && (
                <>
                  <div>
                    <span className="text-sm text-muted-foreground">Tipo de Documento</span>
                    <p className="font-medium mt-1">{entrada.cabecalho.documento_tipo}</p>
                  </div>
                  {entrada.cabecalho.documento_numero && (
                    <div>
                      <span className="text-sm text-muted-foreground">Número</span>
                      <p className="font-medium mt-1">{entrada.cabecalho.documento_numero}</p>
                    </div>
                  )}
                  {entrada.cabecalho.documento_serie && (
                    <div>
                      <span className="text-sm text-muted-foreground">Série</span>
                      <p className="font-medium mt-1">{entrada.cabecalho.documento_serie}</p>
                    </div>
                  )}
                </>
              )}
              {entrada.cabecalho.chave_nfe && (
                <div className="col-span-2">
                  <span className="text-sm text-muted-foreground">Chave NFe</span>
                  <p className="font-mono text-sm mt-1">{entrada.cabecalho.chave_nfe}</p>
                </div>
              )}
              {entrada.cabecalho.observacao && (
                <div className="col-span-2">
                  <span className="text-sm text-muted-foreground">Observações</span>
                  <p className="text-sm mt-1">{entrada.cabecalho.observacao}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Itens */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Itens da Entrada ({entrada.itens.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produto</TableHead>
                      <TableHead className="text-right">Qtd</TableHead>
                      <TableHead className="text-right">Custo Unit.</TableHead>
                      <TableHead className="text-right">Outras Despesas</TableHead>
                      <TableHead className="text-right">Custo Total</TableHead>
                      <TableHead>Lote</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entrada.itens.map((item) => (
                       <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.produto_nome}</TableCell>
                        <TableCell className="text-right">{item.quantidade_compra}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.custo_unitario_compra)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.outras_despesas_item || 0)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.custo_unitario_compra + (item.outras_despesas_item || 0))}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {item.lote || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Detalhes de Embalagem - Exibir apenas na visualização detalhada */}
                <div className="mt-4 space-y-4 border-t pt-4">
                  <h4 className="text-sm font-semibold">Informações de Embalagem</h4>
                  {entrada.itens.map((item) => (
                    <div key={item.id} className="bg-muted/30 p-4 rounded-md">
                      <p className="font-medium mb-3">{item.produto_nome}</p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <span className="text-xs text-muted-foreground">Tipo de Embalagem</span>
                          <p className="font-medium mt-1">{item.tipo_embalagem}</p>
                        </div>
                        {item.peso_individual_g && (
                          <div>
                            <span className="text-xs text-muted-foreground">Peso Individual</span>
                            <p className="font-medium mt-1">{item.peso_individual_g} g</p>
                          </div>
                        )}
                        {item.volume_individual_ml && (
                          <div>
                            <span className="text-xs text-muted-foreground">Volume Individual</span>
                            <p className="font-medium mt-1">{item.volume_individual_ml} ml</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Totais */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Totais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-medium">
                  {formatCurrency(entrada.totais.subtotal_compra)}
                </span>
              </div>
              {entrada.totais.desconto_total_valor && entrada.totais.desconto_total_valor > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Desconto</span>
                  <span className="text-destructive">
                    -{formatCurrency(entrada.totais.desconto_total_valor)}
                  </span>
                </div>
              )}
              {entrada.totais.frete_total && entrada.totais.frete_total > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Frete</span>
                  <span>{formatCurrency(entrada.totais.frete_total)}</span>
                </div>
              )}
              {entrada.totais.outras_despesas_total && entrada.totais.outras_despesas_total > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Outras Despesas</span>
                  <span>{formatCurrency(entrada.totais.outras_despesas_total)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between text-lg font-bold">
                <span>Total Geral</span>
                <span>{formatCurrency(entrada.totais.total_geral_documento)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
          <PermissionGate resource="stock_entries" action="edit">
            <Button onClick={onEdit}>Editar Entrada</Button>
          </PermissionGate>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
